<template>
    <div class="club event-overview">
        <div class="content">
            <div class="events">
                <div class="events-header">
                    <div class="title-container">
                        <h1>Events</h1>
                        <DInput
                            v-model="search"
                            :search="true"
                            :placeholder="'Search'"
                            style="margin-bottom: 0"
                            aria-autocomplete="none"
                        ></DInput>
                        <Button
                            type="primary"
                            icon="plus"
                            :value="$t('event_overview.create_event')"
                            round
                            @click="$router.push({ name: 'event-new', params: { id: $route.params.id } })"
                        />
                    </div>
                    <div v-if="false" class="search-container">
                        <div class="search">
                            <FeatherIcon name="search" />
                            <input type="text" placeholder="34 Events durchsuchen..." />
                        </div>
                        <div class="filter-container">
                            <div class="filter">
                                <div class="text copy-m">Anstehende Veranstaltungen</div>
                                <FeatherIcon name="chevron-down" />
                            </div>
                        </div>
                    </div>
                </div>

                <div v-if="signedInUser && !$apollo.loading && !events.length" class="empty">
                    <EmptyState
                        :message="{
                            title: $t('event_overview.empty_state.title'),
                            description: $t('event_overview.empty_state.description'),
                        }"
                        type="events"
                    >
                        <Button v-if="false" value="Mit Facebook verbinden" type="secondary" round />
                        <Button
                            :value="$t('event_overview.create_event')"
                            type="primary"
                            icon="plus"
                            round
                            @click="$router.push({ name: 'event-new', params: { id: $route.params.id } })"
                        />
                    </EmptyState>
                </div>

                <div class="list event-list">
                    <div v-if="!signedInUser || $apollo.loading" class="loader event-loader">
                        <Event v-for="event in new Array(5)" :key="event" :loading="true" />
                    </div>
                    <div v-if="signedInUser || !$apollo.loading" class="events">
                        <Event
                            v-for="event in events"
                            :key="event.id"
                            :data="event"
                            :loading="$apollo.loading"
                            @click="$router.push({ name: 'event', params: { event_id: event.id } })"
                            @delete="openDeleteModal"
                            @publish="publishEvent"
                            @unpublish="unpublishEvent"
                            @cancelEvent="cancelEvent"
                        />
                    </div>

                    <Modal
                        v-if="deleteModalOpen"
                        class="delete-event-modal"
                        @cancel="deleteModalOpen = false"
                        @confirm="deleteEvent"
                    >
                        <div class="modal-content">
                            <div class="headline-container">
                                <div class="copy-l bold">{{ $t('event_overview.delete_modal.delete_event') }}</div>
                            </div>
                            <div class="main">
                                <div class="copy-m">
                                    {{ $t('event_overview.delete_modal.description') }}
                                </div>
                            </div>
                            <div class="action-container fullwidth">
                                <Button
                                    type="secondary"
                                    :value="$t('event_overview.delete_modal.cancel')"
                                    @click="deleteModalOpen = false"
                                />
                                <Button
                                    type="red"
                                    :value="$t('event_overview.delete_modal.delete')"
                                    @click="deleteEvent"
                                />
                            </div>
                        </div>
                    </Modal>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Event from '@/components/Event'
import Button from '@/components/Button'
import FeatherIcon from '@/components/FeatherIcon'
import gql from 'graphql-tag'
import Modal from '@/components/Modal'
import EmptyState from '@/components/EmptyState'
import DInput from '@/components/Input'

export default {
    name: 'EventOverview',
    components: {
        Event,
        Button,
        FeatherIcon,
        Modal,
        EmptyState,
        DInput,
    },
    data() {
        return {
            eventsPlaceholder: new Array(3),
            eventsLoading: true,
            deleteModalOpen: false,
            isRootAdmin: undefined,
            signedInUser: '',
            search: '',
        }
    },
    apollo: {
        signedInUser: {
            query: gql`
                query {
                    signedInUser {
                        isRootAdmin
                    }
                }
            `,
        },
        events: {
            query: gql`
                query ($filter: EventsFilter, $search: String, $sortBy: EventsSortCriteria, $offset: Int) {
                    events(
                        filter: $filter
                        search: $search
                        sortBy: $sortBy
                        limit: 100
                        offset: $offset
                        sortDirection: Ascending
                    ) {
                        id
                        name
                        coverImageUrl
                        sourceAttendingCount
                        genres
                        sourceInterestedCount
                        startDate
                        endDate
                        status
                    }
                }
            `,
            variables() {
                return {
                    filter: {
                        managedBySignedInBusinessUser: this.signedInUser.isRootAdmin ? false : true,
                        startDateAfter: new Date(),
                        status: ['Published', 'Draft', 'Canceled'],
                    },
                    search: this.search,
                    sortBy: 'Date',
                }
            },
        },
    },
    methods: {
        async unpublishEvent(event) {
            event.status = 'Draft'
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: event.id,
                        value: { status: 'Draft' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', `Event konnte nicht unveröffentlicht werden.`)
                })
        },
        async cancelEvent(event) {
            event.status = 'Canceled'
            await this.$apollo.mutate({
                mutation: gql`
                    mutation ($id: ID!, $value: PartialEventInput!) {
                        updateEvent(id: $id, input: $value) {
                            status
                        }
                    }
                `,
                variables: {
                    id: event.id,
                    value: { status: 'Canceled' },
                },
            })
        },
        async publishEvent(event) {
            event.status = 'Published'
            await this.$apollo
                .mutate({
                    mutation: gql`
                        mutation ($id: ID!, $value: PartialEventInput!) {
                            updateEvent(id: $id, input: $value) {
                                status
                            }
                        }
                    `,
                    variables: {
                        id: event.id,
                        value: { status: 'Published' },
                    },
                })
                .catch(() => {
                    this.$pm.createToast('error', `Event konnte nicht veröffentlicht werden.`)
                })
        },
        kFormatter(num) {
            return Math.abs(num) > 999
                ? Math.sign(num) * (Math.abs(num) / 1000).toFixed(1) + 'k'
                : Math.sign(num) * Math.abs(num)
        },
        openDeleteModal() {
            this.deleteModalOpen = true
        },
        async deleteEvent() {
            this.deleteModalOpen = false

            // TODO: Delete event mutation
            // await this.$apollo.mutate({
            //     mutation: gql`mutation($id: ID!, $value: PartialEventInput!) {
            //           updateEvent(id: $id, input: $value) {
            //             ${name}
            //           }
            //         }
            //         `,
            //     variables: {
            //         id: venueId,
            //         value: eventInput
            //     }
            // });
        },
    },
}
</script>

<style scoped lang="sass">
.event-overview
    .cards-container
        display: flex
        margin-bottom: $xl

        .card
            &.club
                flex: 3
                display: flex
                justify-content: space-between
                align-items: center
                background: $neutral-black

                .text-container
                    margin-bottom: $l

                    .label
                        color: $neutral-1
                        margin-bottom: $xxs

                    h1
                        color: white

                .club-image
                    border-radius: 11px
                    width: 110px
                    height: 110px
                    background-size: cover

            &.stats
                flex: 1
                background: $yellow

                .stat + .stat
                    margin-top: $l

                .stat
                    display: flex
                    align-items: center

                    .number
                        font-size: 3.4rem
                        margin-right: $s
                        color: $neutral-black

                    .label
                        color: $neutral-black


    .events
        .delete-event-modal ::v-deep
            .modal-container
                top: 30%

            .modal-content
                max-width: 348px

        .events-header
            margin-bottom: $xl

            .title-container
                margin-bottom: $l

            .search-container
                border-bottom: 1px solid $gray-1

.search-container
    height: 56px
    display: flex
    align-items: center

    .search
        display: flex
        flex: 1
        height: 100%
        align-items: center

        .feather-icon
            width: $m
            margin-right: $s

            &::v-deep
                svg
                    stroke: $neutral-2

        input
            background: none
            border: none
            height: 100%
            flex: 1
            outline: none
            @extend %copy-m
            +placeholder
                color: $neutral-2

            &:focus
                +placeholder
                    color: lighten($neutral-2, 10%)

    .filter-container
        cursor: pointer
        color: $neutral-2

        .filter
            display: flex
            align-items: center

            .text
                color: currentColor

            .feather-icon
                width: $m
                margin-left: $s

                &::v-deep
                    svg
                        stroke: $neutral-2
</style>
